import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../button/button.component';
import { combineLatest, Subscription } from 'rxjs';
import { MigrationsCucAutoAttendantsContext } from '../../../services/cisco/migrations-cuc-auto-attendants.context';
import {
  CallHandler,
  CiscoPbxExport,
  CucAutoAttendantsReport,
  CucmDirectoryNumbersReport,
} from '../../../../shared/models/generated/ziroModels';
import { TranslateService } from '@ngx-translate/core';
import { CISCO_TIMEZONE_MAP } from '../../../../shared/models/cisco-timezone-map';
import { MigrationsCucmDirectoryNumberContext } from '../../../services/cisco/migrations-cucm-directory-number.context';
import { MigrationsAssessmentsContext } from '../../../services/migrations-assessments.context';

export interface PlayAudioRequestedPayload {
  autoAttendantId: string;
  filename: string;
  url: string;
}

@Component({
  selector: 'ziro-migrations-auto-attendants-detailed-view',
  standalone: false,
  templateUrl: './migrations-auto-attendants-detailed-view.component.html',
  styleUrl: './migrations-auto-attendants-detailed-view.component.scss',
})
export class MigrationsAutoAttendantsDetailedViewComponent implements OnInit, OnDestroy {
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _migrationsCucAutoAttendantsContext = inject(MigrationsCucAutoAttendantsContext);
  private _migrationsCucmDirectoryNumberContext = inject(MigrationsCucmDirectoryNumberContext);
  private _translateService = inject(TranslateService);

  private _subs = new Subscription();

  private _currentAudio: HTMLAudioElement | null = null;
  private _tenantId: string;

  activeGreetingAudioUrl: string | null = null;
  activeGreetingAudioLoadingUrl: string | null = null;
  activeGreetingAudioDownloadUrl: string | null = null;

  ziroIcons = ZiroIcons;
  autoAttendantsReport: CucAutoAttendantsReport | null = null;
  directoryNumbersReport: CucmDirectoryNumbersReport | null = null;
  callHandlers: CallHandler[] = [];
  currentCallHandlerId = '';
  collapsed = false;
  chartLoading = true;
  assessment: CiscoPbxExport;
  timezone: string;

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.paramMap.get('tenantId');
    this._currentAudio?.pause();
    this._initBottomNav();

    const migrationsSub = combineLatest([
      this._migrationsCucAutoAttendantsContext.state$,
      this._migrationsAssessmentsContext.state$,
      this._migrationsCucmDirectoryNumberContext.state$,
    ]).subscribe(([autoAttendantsReport, assessments, directoryNumbersReport]) => {
      this.autoAttendantsReport = autoAttendantsReport;
      this.directoryNumbersReport = directoryNumbersReport;
      this.chartLoading = false;
      this.callHandlers = autoAttendantsReport.callHandlers.sort(
        (a, b) => +!!b.extension - +!!a.extension || (a.displayName || '').localeCompare(b.displayName || ''),
      );
      this.assessment = assessments.ciscoPbxExports.find(
        (assessment) => assessment.id === this._route.snapshot.params['id'],
      );

      this._updateBreadcrumbsAndTimezone();
    });

    const routeSub = this._route.queryParams.subscribe((params) => {
      this.currentCallHandlerId = params.subchart;
      this._updateBreadcrumbsAndTimezone();
    });

    this._subs.add(routeSub);
    this._subs.add(migrationsSub);
  }

  ngOnDestroy(): void {
    this._currentAudio?.pause();
    this._subs.unsubscribe();
  }

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
  }

  backToList(): void {
    this._router.navigate(['..'], { relativeTo: this._route });
  }

  navigateToHandler(id: string): void {
    if (!id || id === this.currentCallHandlerId) {
      return;
    }

    this._router.navigate([], {
      queryParams: { subchart: id },
      queryParamsHandling: 'replace',
      relativeTo: this._route,
    });
  }

  private _updateBreadcrumbsAndTimezone(): void {
    if (!this.autoAttendantsReport || !this.currentCallHandlerId) {
      return;
    }

    const callHandler = this.callHandlers.find((h) => h.id === this.currentCallHandlerId);

    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: this.assessment.name,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this.assessment.id}`,
      },
      {
        label: 'tkey;migrations.cisco.auto_attendants_report.table.auto_attendants',
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this.assessment.id}/cuc-auto-attendants-report`,
      },
      { label: callHandler.displayName },
    ]);

    const handlerTimezone = Number(callHandler.handlerInformation['timeZone']);
    if (handlerTimezone && !isNaN(handlerTimezone) && CISCO_TIMEZONE_MAP[handlerTimezone]) {
      this.timezone = CISCO_TIMEZONE_MAP[handlerTimezone];
    } else {
      this.timezone = null;
    }
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'cuc-auto-attendants-back-button',
          dataAutomation: 'assessments-cisco-auto-attendants-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => this._router.navigate(['..'], { relativeTo: this._route }),
        },
      ]),
    );
  }

  playGreetingAudio(event: string): void {
    const greetingAudioDetails = this.parseRequestPayload(event);

    if (this._currentAudio) {
      this.onStopRequested();
    }

    this.activeGreetingAudioLoadingUrl = greetingAudioDetails.url;

    const objectUrl = this._migrationsCucAutoAttendantsContext.getAudioFileUrl(
      greetingAudioDetails.autoAttendantId,
      greetingAudioDetails.filename,
    );
    const audio = new Audio(objectUrl);

    const cleanup = (): void => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('loadeddata', onLoadedData);
      URL.revokeObjectURL(objectUrl);

      if (this._currentAudio === audio) {
        this._currentAudio = null;
        this.activeGreetingAudioUrl = null;
      }
      if (this.activeGreetingAudioLoadingUrl === greetingAudioDetails.url) {
        this.activeGreetingAudioLoadingUrl = null;
      }
    };

    const onEnded = (): void => {
      return cleanup();
    };
    const onPause = (): void => {
      return cleanup();
    };

    const onLoadedData = (): void => {
      audio.removeEventListener('loadeddata', onLoadedData);

      if (this._currentAudio === audio && this.activeGreetingAudioLoadingUrl === greetingAudioDetails.url) {
        this.activeGreetingAudioLoadingUrl = null;
      }

      audio.play().catch(() => {
        return cleanup();
      });
    };

    this._currentAudio = audio;
    this.activeGreetingAudioUrl = greetingAudioDetails.url;

    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('loadeddata', onLoadedData);
  }

  onStopRequested(): void {
    this._currentAudio.pause();
  }

  downloadGreetingAudio(event: string): void {
    const greetingAudioDetails = this.parseRequestPayload(event);
    const callHandler = this.callHandlers.find((h) => h.id === greetingAudioDetails.autoAttendantId);

    this.activeGreetingAudioDownloadUrl = greetingAudioDetails.url;

    const downloadSub = this._migrationsCucAutoAttendantsContext
      .getAudioFileAsBlob(greetingAudioDetails.autoAttendantId, greetingAudioDetails.filename)
      .subscribe({
        next: (blob: Blob) => {
          const enhancedFilename = `${callHandler.displayName}_${greetingAudioDetails.filename}`;

          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = enhancedFilename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);

          this.activeGreetingAudioDownloadUrl = null;
        },
        error: (error: Error) => {
          this.activeGreetingAudioDownloadUrl = null;
          throw error;
        },
      });

    this._subs.add(downloadSub);
  }

  parseRequestPayload(path: string): PlayAudioRequestedPayload {
    if (!path) {
      throw new Error(
        this._translateService.instant(
          'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.call_handler_empty_audio_path_error',
        ),
      );
    }
    const segments = path.split('/').filter(Boolean);
    const audioFilesIdx = segments.lastIndexOf('audioFiles');
    const filename = audioFilesIdx >= 0 ? segments[audioFilesIdx + 1] : null;
    const autoAttendantId = audioFilesIdx >= 2 ? segments[audioFilesIdx - 1] : null;
    if (!filename || !autoAttendantId) {
      return null;
    }
    return { autoAttendantId, filename, url: path };
  }
}
